import { type ReactElement, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { ImagePlaceholder } from './FeaturePageLayout';

interface Benefit {
  title: string;
  description: string;
}

interface UseCase {
  title: string;
  description: string;
}

interface SolutionPageLayoutProps {
  // Hero
  badge: string;
  title: string;
  subtitle: string;
  heroImage?: string;
  /** Beschreibt, was auf dem Bild zu sehen ist. Ohne ihn stand der
   *  Seitentitel im alt-Attribut — fuer Screenreader wenig hilfreich. */
  heroImageAlt?: string;
  /** Bild im Intro-Abschnitt. Vorgabe: das Dashboard. */
  introImage?: string;
  /** Bild im Funktionsabschnitt. Vorgabe: die Rechnungsliste. */
  featureImage?: string;

  // Intro Section
  introTitle?: string;
  introText?: string;
  introBullets?: string[];

  // Benefits
  benefits?: Benefit[];

  // Use Cases
  useCases?: UseCase[];

  // Features highlight
  featuresTitle?: string;
  featuresDescription?: string;
  features?: string[];

  // Pricing recommendation
  recommendedPlan?: {
    name: string;
    price: string;
    description: string;
  };

  // Testimonial
  testimonial?: {
    quote: string;
    author: string;
    role: string;
    company: string;
  };

  children?: ReactNode;
}

export default function SolutionPageLayout({
  badge,
  title,
  subtitle,
  heroImage,
  heroImageAlt = 'Screenshot der Saldify-Anwendung',
  introImage = '/screenshots/app-dashboard.webp',
  featureImage = '/screenshots/app-rechnungen.webp',
  introTitle,
  introText,
  introBullets,
  benefits,
  useCases,
  featuresTitle = 'Die wichtigsten Funktionen',
  featuresDescription,
  features,
  recommendedPlan,
  testimonial,
  children,
}: SolutionPageLayoutProps): ReactElement {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-primary-50 via-white to-secondary-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-100/50 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary-100/50 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-primary-100 text-primary-700 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
                {badge}
              </span>
              <h1 className="text-4xl lg:text-5xl font-bold text-secondary-900 mb-6">
                {title}
              </h1>
              <p className="text-lg text-secondary-600 mb-8">
                {subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/#preise"
                  className="btn-primary px-6 py-3 shadow-lg shadow-primary-600/30"
                >
                  Kostenlos testen
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/funktionen"
                  className="btn-outline px-6 py-3"
                >
                  Alle Funktionen
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl p-4">
                {heroImage ? (
                  <img src={heroImage} alt={heroImageAlt} className="rounded-xl w-full" />
                ) : (
                  <ImagePlaceholder label={`Screenshot: ${badge}`} aspectRatio="16/10" />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      {(introTitle || introText) && (
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-secondary-900 mb-6">
                  {introTitle}
                </h2>
                <p className="text-lg text-secondary-600 mb-6">
                  {introText}
                </p>
                {introBullets && (
                  <ul className="space-y-3">
                    {introBullets.map((bullet, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                        <span className="text-secondary-700">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="bg-secondary-50 rounded-2xl p-6">
                <img src={introImage} alt={`${title} – Übersicht in Saldify`} className="w-full rounded-xl" loading="lazy" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Benefits Grid */}
      {benefits && benefits.length > 0 && (
        <section className="py-16 lg:py-24 bg-secondary-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 mb-4">
                Ihre Vorteile mit Saldify
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow"
                >
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                    <Check className="w-5 h-5 text-primary-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-secondary-600 text-sm">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Use Cases */}
      {useCases && useCases.length > 0 && (
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 mb-4">
                So nutzen andere Saldify
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {useCases.map((useCase, index) => (
                <div
                  key={index}
                  className="border border-secondary-200 rounded-xl p-6 hover:border-primary-200 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                    {useCase.title}
                  </h3>
                  <p className="text-secondary-600">
                    {useCase.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features List */}
      {features && features.length > 0 && (
        <section className="py-16 lg:py-24 bg-secondary-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-secondary-900 mb-4">
                  {featuresTitle}
                </h2>
                {featuresDescription && (
                  <p className="text-lg text-secondary-600 mb-6">
                    {featuresDescription}
                  </p>
                )}
                <ul className="space-y-3">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                      <span className="text-secondary-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <Link
                    to="/funktionen"
                    className="text-primary-600 font-medium inline-flex items-center gap-1 hover:gap-2 transition-all"
                  >
                    Alle Funktionen entdecken
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-xl p-4">
                <img src={featureImage} alt={`${title} – Funktionen in Saldify`} className="w-full rounded-xl" loading="lazy" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Testimonial */}
      {testimonial && (
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-primary-50 rounded-2xl p-8 lg:p-12">
              <svg className="w-12 h-12 text-primary-300 mx-auto mb-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
              </svg>
              <p className="text-xl lg:text-2xl text-secondary-800 mb-6 italic">
                "{testimonial.quote}"
              </p>
              <div>
                <p className="font-semibold text-secondary-900">{testimonial.author}</p>
                <p className="text-secondary-600 text-sm">
                  {testimonial.role}, {testimonial.company}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Recommended Plan */}
      {recommendedPlan && (
        <section className="py-16 lg:py-24 bg-secondary-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-secondary-900 mb-4">
              Unser Empfehlung für Sie
            </h2>
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-primary-200 mt-8">
              <span className="inline-block bg-primary-100 text-primary-700 px-3 py-1 rounded-full text-sm font-medium mb-4">
                Empfohlen
              </span>
              <h3 className="text-2xl font-bold text-secondary-900 mb-2">
                {recommendedPlan.name}
              </h3>
              <p className="text-4xl font-bold text-primary-600 mb-2">
                {recommendedPlan.price}
              </p>
              <p className="text-secondary-600 mb-6">
                {recommendedPlan.description}
              </p>
              <Link
                to="/#preise"
                className="btn-primary px-8 py-3"
              >
                Jetzt starten
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Additional Content */}
      {children}

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Bereit durchzustarten?
          </h2>
          <p className="text-lg text-primary-100 mb-8">
            Testen Sie Saldify 14 Tage kostenlos und unverbindlich.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/#preise"
              className="bg-white text-primary-600 hover:bg-primary-50 px-8 py-4 rounded-lg font-semibold transition-all shadow-lg inline-flex items-center justify-center gap-2"
            >
              Kostenlos starten
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="#kontakt"
              className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 rounded-lg font-semibold transition-all inline-flex items-center justify-center"
            >
              Beratung anfordern
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
